import { FC, memo, useEffect, useState } from 'react';

import { useMediaQuery } from '@mui/material';

import CompanyCard from '@components_iqfu/public/company_card/CompanyCard';
import HowToChooseCompanies from '@components_iqfu/public/how_to_choose_companies/HowToChooseCompanies';
import NotFoundCompanies from '@components_iqfu/public/not_found_companies/NotFoundCompanies';
import RequestCompaniesSearchToAdmin from '@components_iqfu/public/request_companies_search_to_admin/RequestCompaniesSearchToAdmin';

import { CityInfo } from '@type-def_iqfu/public/CityDetailData';
import { CompanyListItem } from '@type-def_iqfu/public/CompanyListItem';

import { MediaQuery } from '@constants_common';
import styles from '@styles_iqfu/public/companies.module.scss';
import { CompaniesPageParameter, CompaniesPageQueryType } from 'src/iqfu/data/companiesPagesData';

import InternalLinksInRanking from '../internal_links_in_ranking/InternalLinksInRanking';

type Props = {
  displayCompaniesData: CompanyListItem[] | undefined;
  cityInfo: CityInfo;
  pageRenderParameter: CompaniesPageParameter;
  setFloatingThresholdAfterRender: (node: HTMLHeadingElement) => Promise<void>;
};

const CompaniesCitiesCompanyRanking: FC<Props> = memo(
  ({ displayCompaniesData, cityInfo, pageRenderParameter, setFloatingThresholdAfterRender }) => {
    const queryTypeString = pageRenderParameter.query_type_string;
    const pageQueryType = pageRenderParameter.page_query_type;
    const isMobile = useMediaQuery(MediaQuery.lg);

    const companiesCount = displayCompaniesData?.length ?? 0;

    const requestCompaniesSearchToAdminBoundary = 6;

    const renderCompanyCards = (companies: CompanyListItem[], startIndex = 0) => {
      return (
        <>
          {companies.map((company, i) => (
            <CompanyCard
              key={i}
              data={company}
              rank={i + startIndex + 1}
              pageQuery={pageQueryType}
              conversionType={'direct_chat_link'}
              displayCityName={cityInfo.city.name}
            />
          ))}
        </>
      );
    };

    // 会社が0社のとき
    if (!displayCompaniesData || displayCompaniesData.length < 1) {
      return (
        <>
          <NotFoundCompanies showDefaultLinks={false} />
        </>
      );
    }

    return (
      <>
        <div className={styles.company_cities_rank_area}>
          {companiesCount > 0 && (
            <>
              <div className={styles.company_cards_container}>
                {/* ランキング1位から2位まで表示 */}
                {renderCompanyCards(displayCompaniesData.slice(0, Math.min(2, companiesCount)), 0)}

                {/* モバイルで会社5社以上の場合、ここまでスクロールしたらフローティングを表示する */}
                {isMobile && companiesCount >= 5 && (
                  <div ref={setFloatingThresholdAfterRender} className={styles.company_rankings_negative_spacer} />
                )}

                {/* ランキング3位を表示 */}
                {renderCompanyCards(displayCompaniesData.slice(2, Math.min(3, companiesCount)), 2)}
              </div>

              {companiesCount >= 3 && (
                <div className={styles.ranking_middle_area}>
                  <HowToChooseCompanies type={pageQueryType ? pageQueryType : 'default'} />
                </div>
              )}

              {companiesCount >= requestCompaniesSearchToAdminBoundary && (
                <RequestCompaniesSearchToAdmin
                  type={pageQueryType ? pageQueryType : 'default'}
                  cityName={cityInfo.city.name}
                />
              )}

              {/* ランキング4位から6位まで表示 */}
              {companiesCount >= 4 && (
                <div className={styles.company_cards_container}>
                  {renderCompanyCards(displayCompaniesData.slice(3, Math.min(6, companiesCount)), 3)}
                </div>
              )}

              {/* 回遊リンクを表示 */}
              <div className={styles.ranking_middle_area}>
                <InternalLinksInRanking
                  cityInfo={cityInfo}
                  queryTypeString={queryTypeString}
                  pageQueryType={pageQueryType}
                />
              </div>

              {companiesCount < requestCompaniesSearchToAdminBoundary && (
                <RequestCompaniesSearchToAdmin
                  type={pageQueryType ? pageQueryType : 'default'}
                  cityName={cityInfo.city.name}
                />
              )}

              {/* ランキング7位から10位まで表示 */}
              {companiesCount >= 7 && (
                <div className={styles.company_cards_container}>
                  {renderCompanyCards(displayCompaniesData.slice(6, Math.min(10, companiesCount)), 6)}
                </div>
              )}

              {/* ランキング11社以上の場合、もっと見るエリアを表示 */}
              {companiesCount >= 11 && (
                <ShowMoreCompanies
                  displayCompaniesData={displayCompaniesData}
                  cityInfo={cityInfo}
                  pageQueryType={pageQueryType}
                />
              )}
            </>
          )}
        </div>
      </>
    );
  },
);

type ShowMoreCompaniesProps = {
  displayCompaniesData: CompanyListItem[];
  cityInfo: CityInfo;
  pageQueryType: CompaniesPageQueryType;
};

const ShowMoreCompanies: FC<ShowMoreCompaniesProps> = ({ displayCompaniesData, cityInfo, pageQueryType }) => {
  const [visibleCompaniesCount, setVisibleCompaniesCount] = useState(10);
  //同階層遷移時に状態をリセット
  useEffect(() => {
    setVisibleCompaniesCount(10);
  }, [cityInfo]);

  const readMoreCompaniesButtonHandler = () => {
    setVisibleCompaniesCount(visibleCompaniesCount + 10);
  };

  const companiesCount = displayCompaniesData?.length ?? 0;
  // loadCompaniesCountIndex: 「もっと見る」でロードした会社の数(添字であることに注意)
  const loadCompaniesCountIndex = companiesCount - 11;
  return (
    <div className={styles.read_more_companies_area}>
      <div className={styles.company_cards_container}>
        <>
          {[...displayCompaniesData].slice(10, visibleCompaniesCount).map((company, i) => {
            return (
              <>
                <CompanyCard
                  key={i}
                  data={company}
                  rank={undefined}
                  pageQuery={pageQueryType}
                  conversionType={'direct_chat_link'}
                  displayCityName={cityInfo.city.name}
                />
                {/* iは配列の添字であることに注意 */}
                {/* 5社目の下に「紹介してもらう」パーツを表示 */}
                {i % 10 === 4 && (
                  <RequestCompaniesSearchToAdmin
                    type={pageQueryType ? pageQueryType : 'default'}
                    cityName={cityInfo.city.name}
                  />
                )}
                {/* 「もっと見る」で最後の会社をロードし、それが1-4社目である場合 */}
                {i === loadCompaniesCountIndex && i % 10 <= 3 && (
                  <RequestCompaniesSearchToAdmin
                    type={pageQueryType ? pageQueryType : 'default'}
                    cityName={cityInfo.city.name}
                  />
                )}
              </>
            );
          })}
          {displayCompaniesData.length > visibleCompaniesCount && (
            <button className={styles.read_more_companies_button} onClick={() => readMoreCompaniesButtonHandler()}>
              ランキングの続きを見る
              <span className={styles.read_more_companies_button_icon}></span>
            </button>
          )}
        </>
      </div>
    </div>
  );
};

CompaniesCitiesCompanyRanking.displayName = 'CompaniesCitiesCompanyRanking';
export default CompaniesCitiesCompanyRanking;
